import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  LayoutDashboard,
  Calendar,
  MessageSquare,
  LogOut,
  Mail,
  Phone
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminContacts = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);

  const fetchContacts = useCallback(async () => {
    try {
      const response = await fetch(`${API}/admin/contact-submissions`, { credentials: 'include' });
      const data = await response.json();
      setContacts(data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching contacts:', error);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  return (
    <div data-testid="admin-contacts-page" className="min-h-screen bg-[#FAF8F5]">
      {/* Header */}
      <div className="bg-[#1A1A1A] text-[#FAF8F5] py-4 px-6 md:px-12">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="font-serif text-2xl font-bold tracking-tight text-[#C9A84C]">
              De Azraa
            </h1>
            <span className="text-xl font-medium">Admin Dashboard</span>
          </div>
          <div className="flex items-center gap-6">
            <span className="text-sm">{user?.email}</span>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="flex items-center gap-2 text-sm hover:text-[#C9A84C] transition-colors"
            >
              <LogOut size={16} />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="bg-white border-b border-[#E5E5E5] px-6 md:px-12 py-4">
        <nav className="flex gap-8">
          <Link
            to="/admin/dashboard"
            data-testid="nav-dashboard"
            className="flex items-center gap-2 text-sm font-medium text-[#3A3A3A] hover:text-[#C9A84C] transition-colors pb-2"
          >
            <LayoutDashboard size={16} />
            Dashboard
          </Link>
          <Link
            to="/admin/bookings"
            data-testid="nav-bookings"
            className="flex items-center gap-2 text-sm font-medium text-[#3A3A3A] hover:text-[#C9A84C] transition-colors pb-2"
          >
            <Calendar size={16} />
            Bookings
          </Link>
          <Link
            to="/admin/suites"
            data-testid="nav-suites"
            className="flex items-center gap-2 text-sm font-medium text-[#3A3A3A] hover:text-[#C9A84C] transition-colors pb-2"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
              <polyline points="9 22 9 12 15 12 15 22"/>
            </svg>
            Suites
          </Link>
          <Link
            to="/admin/contacts"
            data-testid="nav-contacts"
            className="flex items-center gap-2 text-sm font-medium text-[#C9A84C] border-b-2 border-[#C9A84C] pb-2"
          >
            <MessageSquare size={16} />
            Contact Messages
          </Link>
        </nav>
      </div>

      {/* Main Content */}
      <div className="px-6 md:px-12 py-12">
        <div className="bg-white border border-[#E5E5E5] p-6">
          <h2 className="font-serif text-2xl tracking-tight font-light text-[#1A1A1A] mb-6">
            Contact Submissions
          </h2>

          {loading ? (
            <p className="text-center py-8 text-[#3A3A3A]">Loading messages...</p>
          ) : contacts.length === 0 ? (
            <p className="text-center py-8 text-[#3A3A3A]" data-testid="no-contacts">No messages yet</p>
          ) : (
            <div className="space-y-4">
              {contacts.map((contact) => (
                <motion.div
                  key={contact.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: contacts.indexOf(contact) * 0.05 }}
                  data-testid={`contact-card-${contact.id}`}
                  className="border border-[#E5E5E5] p-6 hover:border-[#C9A84C] transition-colors cursor-pointer"
                  onClick={() => setSelectedContact(contact)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-lg font-medium text-[#1A1A1A] mb-1">{contact.name}</h3>
                      <div className="flex items-center gap-4 text-sm text-[#3A3A3A]">
                        <span className="flex items-center gap-1">
                          <Mail size={14} />
                          {contact.email}
                        </span>
                        <span className="flex items-center gap-1">
                          <Phone size={14} />
                          {contact.phone}
                        </span>
                      </div>
                    </div>
                    <span className="text-xs text-[#3A3A3A]">
                      {new Date(contact.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-sm text-[#3A3A3A] line-clamp-2">{contact.message}</p>
                  {contact.check_in && contact.check_out && (
                    <div className="mt-3 text-xs text-[#3A3A3A]">
                      Interested dates: {new Date(contact.check_in).toLocaleDateString()} - {new Date(contact.check_out).toLocaleDateString()}
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Message Details Modal */}
      {selectedContact && (
        <div
          data-testid="contact-modal"
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-6"
          onClick={() => setSelectedContact(null)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white p-8 max-w-2xl w-full"
          >
            <h3 className="font-serif text-2xl tracking-tight font-light text-[#1A1A1A] mb-6">
              Message Details
            </h3>

            <div className="space-y-4 mb-6">
              <div>
                <p className="text-xs uppercase tracking-wider text-[#3A3A3A] mb-1">Name</p>
                <p className="text-[#1A1A1A]">{selectedContact.name}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-[#3A3A3A] mb-1">Email</p>
                <p className="text-[#1A1A1A]">{selectedContact.email}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-[#3A3A3A] mb-1">Phone</p>
                <p className="text-[#1A1A1A]">{selectedContact.phone}</p>
              </div>
              {selectedContact.check_in && selectedContact.check_out && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-[#3A3A3A] mb-1">Check-in</p>
                    <p className="text-[#1A1A1A]">{new Date(selectedContact.check_in).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-[#3A3A3A] mb-1">Check-out</p>
                    <p className="text-[#1A1A1A]">{new Date(selectedContact.check_out).toLocaleDateString()}</p>
                  </div>
                </div>
              )}
              <div>
                <p className="text-xs uppercase tracking-wider text-[#3A3A3A] mb-1">Message</p>
                <p className="text-[#1A1A1A] whitespace-pre-wrap">{selectedContact.message}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-[#3A3A3A] mb-1">Received</p>
                <p className="text-[#1A1A1A]">{new Date(selectedContact.created_at).toLocaleString()}</p>
              </div>
            </div>

            <button
              onClick={() => setSelectedContact(null)}
              data-testid="close-modal-btn"
              className="w-full border border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-[#FAF8F5] transition-colors px-8 py-3 text-sm tracking-widest uppercase"
            >
              Close
            </button>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminContacts;
